import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const MissionSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  return (
    <section id="mission" ref={sectionRef} className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1639322537228-f710d846310a"
          alt="Blockchain Mission"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-800/90"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          style={{ opacity }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Mission
          </motion.h2>

          <motion.div
            style={{ y }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div className="text-left">
              <motion.h3
                className="text-3xl font-semibold mb-6 text-white"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Tokenized Wealth
              </motion.h3>

              <motion.p
                className="text-xl text-gray-300 mb-6 leading-relaxed"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Octagon Infinity is created to generate wealth within our VIP community. We will generate a real estate portfolio, work out our way to different investing opportunities. Find and co-operate with entrepreneurs and give life to innovative ideas. Our primary focus is Real Estate, and each NFT(Card) will be backed by fractional ownership in Real Estate, with a minimum asset value of 4,000€, depending on participation even more.
              </motion.p>

              <motion.div
                className="space-y-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-gray-300">Exclusive access to premium investment opportunities</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                  <span className="text-gray-300">Transparent, blockchain-verified ownership</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-300">Community-driven investment decisions</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300">Sustainable, long-term wealth building</span>
                </div>
              </motion.div>
            </div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 backdrop-blur-lg rounded-3xl p-8 border border-gray-700 hover:border-purple-500 transition-all duration-300">
                <img 
                  src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5"
                  alt="Blockchain Technology"
                  className="w-full h-64 object-cover rounded-2xl mb-6 opacity-80"
                />
                
                <h4 className="text-2xl font-semibold mb-4 text-white">The Octagon Infinity Advantage</h4>
                <p className="text-gray-300 leading-relaxed">
                  Octagon infinity is a VIP Investment Syndicate. Our mission is to generate passive income to all our NFT holders. Participate in tokenizing Real Estate and Innovative Projects. Create, Buy, Own assets and Earn together.
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Mission Stats */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">10,000</div>
              <div className="text-gray-400">NFTs Available</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">€40M</div>
              <div className="text-gray-400">Investment Power</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">12-25%</div>
              <div className="text-gray-400">Target Returns</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">∞</div>
              <div className="text-gray-400">Exclusive Access</div>
            </div>
          </motion.div>

          {/* The Ultimate Goal */}
          <motion.div
            className="max-w-5xl mx-auto mt-20"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-purple-600/10 to-cyan-600/10 backdrop-blur-lg rounded-3xl p-10 border-2 border-purple-500/30 relative overflow-hidden">
              {/* Animated Background Elements */}
              <div className="absolute inset-0 opacity-5">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-6 h-6 bg-cyan-400 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -15, 0],
                      opacity: [0.2, 0.6, 0.2],
                      scale: [1, 1.3, 1],
                    }}
                    transition={{
                      duration: 4 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 3,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10 text-center">
                <motion.p
                  className="text-xl text-gray-200 leading-relaxed mb-8 max-w-3xl mx-auto"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                  viewport={{ once: true }}
                >
                  We leverage cutting-edge AI technology to identify high-potential real estate markets and optimize investment strategies across our portfolio. 
                  Our AI-powered analytics enable us to make data-driven decisions that maximize returns while minimizing risk, ensuring exponential growth for our community.
                </motion.p>

                <motion.p
                  className="text-xl text-gray-200 leading-relaxed mb-8 max-w-3xl mx-auto"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  viewport={{ once: true }}
                >
                  Our mission is to ensure <span className="text-purple-400 font-semibold">every card participates in a project</span>. 
                  Each card will be <span className="text-cyan-400 font-semibold">backed by €4,000 worth of real estate</span>, 
                  making your NFT literally worth <span className="text-green-400 font-bold">€4,000 in tangible assets</span>.
                </motion.p>

                <motion.div
                  className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                  viewport={{ once: true }}
                >
                  <div className="bg-purple-600/20 backdrop-blur-lg rounded-2xl p-6 border border-purple-500/30">
                    <div className="text-3xl font-bold text-purple-400 mb-2">100%</div>
                    <div className="text-white font-semibold mb-2">Card Participation</div>
                    <div className="text-gray-300 text-sm">Every card holder gets project access</div>
                  </div>

                  <div className="bg-cyan-600/20 backdrop-blur-lg rounded-2xl p-6 border border-cyan-500/30">
                    <div className="text-3xl font-bold text-cyan-400 mb-2">€4,000</div>
                    <div className="text-white font-semibold mb-2">Real Estate Backing</div>
                    <div className="text-gray-300 text-sm">Tangible asset value per card</div>
                  </div>

                  <div className="bg-green-600/20 backdrop-blur-lg rounded-2xl p-6 border border-green-500/30">
                    <div className="text-3xl font-bold text-green-400 mb-2">€4,000</div>
                    <div className="text-white font-semibold mb-2">Asset Backing Goal</div>
                    <div className="text-gray-300 text-sm">Real estate value per NFT</div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionSection;